import { formatDistanceToNow } from "date-fns"
import { History } from "lucide-react"
import React, { Suspense, useCallback, useMemo } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"
import { cn } from "src/common/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover"

import type { ThreadSelectorQuery } from "./__generated__/ThreadSelectorQuery.graphql"

interface ThreadSelectorProps {
  activeConversationId: string | null
  onSelectThread: (id: string) => void
  metadataFilter: Record<string, unknown>
  fetchKey: number
}

const threadSelectorQuery = graphql`
  query ThreadSelectorQuery($metadataContains: JSON) {
    me {
      ...appMe
      llmConversations(sources: [UI], metadataContains: $metadataContains) {
        id
        title
        createdAt
        lastUserMessageTime
      }
    }
  }
`

interface ThreadListProps {
  activeConversationId: string | null
  onSelectThread: (id: string) => void
  metadataFilter: Record<string, unknown>
  fetchKey: number
}

const ThreadList = React.memo<ThreadListProps>(function ThreadList({
  activeConversationId,
  onSelectThread,
  metadataFilter,
  fetchKey,
}) {
  const data = useLazyLoadQuery<ThreadSelectorQuery>(
    threadSelectorQuery,
    { metadataContains: metadataFilter },
    { fetchPolicy: "store-and-network", fetchKey }
  )

  const threads = useMemo(() => {
    const conversations = data.me?.llmConversations ?? []
    return [...conversations].sort((a, b) => {
      const timeA = a.lastUserMessageTime ?? a.createdAt
      const timeB = b.lastUserMessageTime ?? b.createdAt
      return new Date(timeB).getTime() - new Date(timeA).getTime()
    })
  }, [data.me?.llmConversations])

  if (threads.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-xs text-slate-400">
        No previous threads
      </div>
    )
  }

  return (
    <div className="max-h-64 overflow-y-auto">
      {threads.map((thread) => {
        const isActive = thread.id === activeConversationId
        const time = thread.lastUserMessageTime ?? thread.createdAt
        const relativeTime = time
          ? formatDistanceToNow(new Date(time), { addSuffix: true })
          : ""

        return (
          <button
            key={thread.id}
            className={cn(
              "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
              isActive
                ? "bg-slate-700 text-white"
                : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
            )}
            onClick={() => onSelectThread(thread.id)}
          >
            <div className="truncate font-medium">{thread.title}</div>
            <div className="text-xs text-slate-400 mt-0.5">{relativeTime}</div>
          </button>
        )
      })}
    </div>
  )
})

export const ThreadSelector = React.memo<ThreadSelectorProps>(
  function ThreadSelector({
    activeConversationId,
    onSelectThread,
    metadataFilter,
    fetchKey,
  }) {
    const [open, setOpen] = React.useState(false)

    const handleSelectThread = useCallback(
      (id: string) => {
        onSelectThread(id)
        setOpen(false)
      },
      [onSelectThread]
    )

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
            title="Thread history"
          >
            <History className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          className="w-72 p-2 bg-slate-800 border-slate-700"
        >
          <div className="text-xs font-medium text-slate-400 px-3 py-1.5">
            Threads
          </div>
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-4">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-600 border-t-electric" />
              </div>
            }
          >
            <ThreadList
              activeConversationId={activeConversationId}
              onSelectThread={handleSelectThread}
              metadataFilter={metadataFilter}
              fetchKey={fetchKey}
            />
          </Suspense>
        </PopoverContent>
      </Popover>
    )
  }
)
